import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, Chip, Divider, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ActionConstruction } from 'models/order-input/action-order';
import AddedMaterialsSummary from 'components/order-flow/shared/constructions/added-materials-summary.component';
import { jawOfRange } from 'components/order-flow/shared/restoration-input-helper';
import { formatTeethList, formatTeethRange } from 'utils/teeth';

type ConstructionBoxProps = {
    construction: ActionConstruction;
    onDeleteClick: (construction: ActionConstruction) => void;
    onModifyClick: (construction: ActionConstruction) => void;
    disableDelete?: boolean;
    disableModify?: boolean;
};
const ConstructionBox: FC<ConstructionBoxProps> = ({
    construction,
    onDeleteClick,
    onModifyClick,
    disableDelete,
    disableModify,
}) => {
    const { t: translate } = useTranslation(['orders', 'construction-props', 'common']);

    const subTitle = () => {
        if (construction.teethNumbers) {
            return formatTeethList(construction.teethNumbers);
        }
        if (!construction.teethRange) {
            return '';
        }
        // The jaw picker holds a jaw selection as a range; name the jaws it stands for.
        const jaw = jawOfRange(construction.teethRange);
        if (jaw) {
            return translate(`orders:${jaw}`);
        }
        const { start, end } = construction.teethRange;
        return formatTeethRange(start, end);
    };

    const camelToUpperSnake = (str: string): string => {
        return str.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
    };

    const categoryNames = () => {
        if (construction.actions.length > 0) {
            return construction.actions[0].subCategory[0].name;
        }
        return '';
    };

    const teethNumbers = (value: { id: number }[]) => {
        return formatTeethList(value.map((tooth) => tooth.id));
    };

    return (
        <Card sx={{ width: '100%' }} variant="outlined">
            <Box px={3} py={2}>
                <Box flexGrow={1} alignContent="center">
                    <Box display="flex" alignItems="center" mb={0.5}>
                        <Box flexGrow={1} display="flex" alignItems="center">
                            <Typography variant="h3">
                                {categoryNames()} {subTitle()}
                            </Typography>
                            {construction.settings?.material ? (
                                <Fragment>
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{ marginLeft: 0.75, marginRight: 0.75 }}
                                    />
                                    <Typography variant="h3">{construction.settings.material as string}</Typography>
                                </Fragment>
                            ) : undefined}
                        </Box>
                        <Box display="flex" alignItems="center">
                            {construction?.isTioTandvard && (
                                <Box mr={1}>
                                    <Typography variant="subtitle2" color="warning.main">
                                        {translate(`orders:TIOTANDVARD`)}
                                    </Typography>
                                </Box>
                            )}
                            {construction?.warranty && (
                                <Box mr={1}>
                                    <Typography variant="subtitle2" color="warning.main">
                                        {translate(`orders:WARRANTY`)}
                                    </Typography>
                                </Box>
                            )}
                            {!disableModify && (
                                <Button color="primary" size="small" onClick={() => onModifyClick(construction)}>
                                    {translate('orders:MODIFY_CONSTRUCTION')}
                                </Button>
                            )}
                            {!disableDelete && (
                                <IconButton size="small" onClick={() => onDeleteClick(construction)}>
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Box>
                    </Box>

                    <Box display="flex" gap={1} mb={0.5} flexWrap="wrap">
                        {construction.actions.map((action) => (
                            <Chip
                                key={action._id}
                                label={
                                    <span>
                                        {`${action.number} - ${action.name}  `}
                                        {action.quotable && (
                                            <Typography
                                                component="strong"
                                                sx={{ fontWeight: 'bold', color: 'warning.main', fontSize: 'inherit' }}
                                            >
                                                {`(${translate('common:QUOTABLE')})`}
                                            </Typography>
                                        )}
                                    </span>
                                }
                            />
                        ))}
                    </Box>

                    {construction.settings ? (
                        <Box>
                            <Box display="flex" flexWrap="wrap">
                                {construction.settings['shade'] ? (
                                    <Box mr={2}>
                                        <Typography variant="caption" sx={{ fontWeight: '600' }}>
                                            {translate(`construction-props:SHADE`)}:
                                        </Typography>{' '}
                                        <Typography variant="caption">
                                            {translate(`${construction.settings['shade']}`, {
                                                defaultValue: construction.settings['shade'] as string,
                                            })}
                                        </Typography>
                                    </Box>
                                ) : undefined}
                                {construction.settings['color'] ? (
                                    <Box mr={2}>
                                        <Typography variant="caption" sx={{ fontWeight: '600' }}>
                                            {translate(`construction-props:COLOR`)}:
                                        </Typography>{' '}
                                        <Typography variant="caption">
                                            {translate(`${construction.settings['color']}`, {
                                                defaultValue: construction.settings['color'] as string,
                                            })}
                                        </Typography>
                                    </Box>
                                ) : undefined}

                                {construction.settings['colorSampling'] ? (
                                    <Box mr={2}>
                                        <Typography variant="caption" sx={{ fontWeight: '600' }}>
                                            {translate(`construction-props:COLOR_SAMPLING_AT_LAB`)}:
                                        </Typography>{' '}
                                        <Typography variant="caption">{translate('common:YES')}</Typography>
                                    </Box>
                                ) : undefined}
                                {Object.entries(construction.settings).map(([key, value]) => (
                                    <Fragment key={key}>
                                        <>
                                            {value &&
                                            key !== 'material' &&
                                            key !== 'materialId' &&
                                            key !== 'color' &&
                                            key !== 'shade' &&
                                            key !== 'colorSampling' &&
                                            key !== 'claspTeeth' &&
                                            key !== 'springTeeth' &&
                                            key !== 'numberOfUnits' ? (
                                                <Box mr={2}>
                                                    <Typography variant="caption" sx={{ fontWeight: '600' }}>
                                                        {translate(`construction-props:${camelToUpperSnake(key)}`)}:{' '}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        {translate(`${value}`, { defaultValue: String(value) })}
                                                    </Typography>
                                                </Box>
                                            ) : undefined}

                                            {(key === 'claspTeeth' || key === 'springTeeth') &&
                                            (
                                                value as {
                                                    id: number;
                                                }[]
                                            )?.length ? (
                                                <Box mr={2}>
                                                    <Typography variant="caption" sx={{ fontWeight: '600' }}>
                                                        {translate(`construction-props:${camelToUpperSnake(key)}`)}:{' '}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        {teethNumbers(value as { id: number }[])}
                                                    </Typography>
                                                </Box>
                                            ) : undefined}

                                            {value && key === 'numberOfUnits' ? (
                                                <>
                                                    {(value as any[]).map((val: any, idx: number) => (
                                                        <Box mr={2} key={idx}>
                                                            <Typography variant="caption" sx={{ fontWeight: '600' }}>
                                                                {val.header}:{' '}
                                                            </Typography>
                                                            <Typography variant="caption">
                                                                {val.numberOfUnits}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </>
                                            ) : undefined}
                                        </>
                                    </Fragment>
                                ))}
                                {Object.entries(construction.settings).map(([key, value]) => (
                                    <Fragment key={key}></Fragment>
                                ))}
                            </Box>
                        </Box>
                    ) : undefined}
                    <AddedMaterialsSummary addedMaterials={construction.addedMaterials} />
                </Box>
            </Box>
        </Card>
    );
};

export default ConstructionBox;
